import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, Users, Plus, BookOpen } from 'lucide-react';

interface EmptyStateProps {
  onStartSetup: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ onStartSetup }) => {
  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6">
      <div className="max-w-4xl w-full space-y-8">
        <div className="text-center space-y-4">
          <div className="mx-auto h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
            <Building2 className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight">Welcome to Your Shelter Management System</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Let's get started by setting up your first facility. It only takes a minute!
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Card className="border-2">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
              <CardTitle className="text-lg">Create Facilities</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-center">
                Set up shelter locations with rooms, capacity, and contact information
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <CardTitle className="text-lg">Manage Clients</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-center">
                Add residents, track check-ins, monitor services, and manage notes
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center mb-3">
                <BookOpen className="h-6 w-6 text-purple-600" />
              </div>
              <CardTitle className="text-lg">Track Services</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-center">
                Monitor daily services, generate reports, and maintain compliance records
              </CardDescription>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
          <Button onClick={onStartSetup} size="lg" className="gap-2 text-lg px-8 h-12">
            <Plus className="h-5 w-5" />
            Set Up Your First Facility
          </Button>
          <Button variant="outline" size="lg" className="gap-2 text-lg px-8 h-12">
            <BookOpen className="h-5 w-5" />
            View Documentation
          </Button>
        </div>

        <Card className="bg-muted/50 border-dashed">
          <CardContent className="pt-6">
            <div className="text-center space-y-2">
              <p className="font-medium">Need Help Getting Started?</p>
              <p className="text-sm text-muted-foreground">
                Our setup wizard will guide you through creating your first facility, setting up rooms, and adding your first clients.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmptyState;
